import { defineComponent, h } from 'vue';
import BaseComponent from './BaseAgGridMixin.jsx';
import utils from '../../Shared/utils.jsx';

import { AgGridVue } from "ag-grid-vue3";

// The listing backends order by raw byte value, so every upper case name sorts ahead of every lower
// case one. A start key typed in the other case lands past the row the user asked for and hides it,
// so fold to the case that sits furthest back along the direction being scanned.
function foldJumpKey(value, descending) {
    if (typeof value !== 'string' || value === '') return value;
    return descending ? value.toLowerCase() : value.toUpperCase();
}

// ListFilesExtV3 (and similar APIs) recognize the literal "%FF%FF" as a high sentinel for the
// first page of a descending scan. Typed values carry it too, appended after the encoding: the
// descending upper bound must sit above every name that extends the typed text, or the row the
// user typed is excluded ("banana.txt" sorts above "banana").
function buildInitialStartKeyList(jumpToValue, descending, additionalKeyFieldCount) {
    const folded = foldJumpKey(jumpToValue, descending) || '';
    const sentinel = descending ? '%ff%ff' : '';

    let keys = '"' + encodeURIComponent(folded) + sentinel + '"';

    for (let x = 0; x < additionalKeyFieldCount; x++)
        keys += ',"' + sentinel + '"';

    return '[' + keys + ']';
}

export default defineComponent({
    name: 'infinite-grid',
    mixins: [BaseComponent],
    components: {
        AgGridVue,
    },
    data: () => ({
        aggrid_ref: null,
        sortDef: null,
        startKeyList: null,
        pageSize: 100,
        allowExportAll: false,
        dataSource: null,
        model_all: [],
    }),
    created() {
        // Find the default sort column or the first one marked sortable
        this.sortDef = this.columnDefs.find(c => (this.controlData.DefaultSort && c.Field == this.controlData.DefaultSort) || (!this.controlData.DefaultSort && c.Sortable));
        if (!this.sortDef) {
            utils.warn(`InfiniteGrid ${this.name} - No default sortable column found - at least one column must be sortable; DataURL: ${this.controlData.DataURL}`);
            return;
        }

        const descending = (this.controlData.SortDirection == "desc");
        const additionalKeyCount = this.sortDef.AdditionalKeyFields ? this.sortDef.AdditionalKeyFields.length : 0;

        this.startKeyList = buildInitialStartKeyList(this.jumpToValue, descending, additionalKeyCount);

        // NOTE: Intentionally left model_all non-reactive by not including in the data properties (above) - large datasets will cause performance issues
        if (!this.model_all) this.model_all = [];

        const c = this;

        this.dataSource = {
            getRows: async (params) => {
                //debugger;

                // sort and filter parameters:
                // params.sortModel   : [{"sort":"asc","colId":"ObjectType"}]
                // params.filterModel
                
                try {
                if (c.modelurl) {
                    let url = c.modelurlvalue;
                    let pageSize = params.endRow - params.startRow; // loadOptions.take;
                    let skipFirst = false;
                    let sortCol = c.sortDef.Field;
                    let descending = c.controlData.SortDirection == "desc";

                    utils.debug(`InfiniteGrid load; params: ${JSON.stringify(params)} name:${c.name} url:${url}...`);
					//utils.debug(`InfiniteGrid load; Options: ${JSON.stringify(loadOptions)} name:${c.name} url:${url}...`);
					//if (loadOptions.filter)
					//	return null;

                    if (params.sortModel && params.sortModel.length > 0) {
                        sortCol = params.sortModel[0].colId;
                        descending = params.sortModel[0].sort == 'desc';

                        c.sortDef = c.columnDefs.find(c => c.Field == sortCol);
                    }
                    //if (loadOptions.sort && loadOptions.sort.length > 0 && typeof loadOptions.sort[0].selector === 'string') {
                    //    sortCol = loadOptions.sort[0].selector;
                    //    descending = loadOptions.sort[0].desc;

                    //    c.sortDef = c.columnDefs.find(c => c.Field == sortCol);
                    //}

                    if (params.startRow === 0) { //(loadOptions.skip === 0) {

                        const additionalKeyCount = c.sortDef.AdditionalKeyFields ? c.sortDef.AdditionalKeyFields.length : 0;
                        c.startKeyList = buildInitialStartKeyList(c.jumpToValue, descending, additionalKeyCount);
                        c.model_all = [];
                    }
                    else if (params.startRow > 0 && c.model_all.length > 0) { //(loadOptions.skip > 0 && c.model_all.length > 0) {
                        // Find the last row that will be skipped or the end of the list, this will be used for the new startKey
                        //const last = c.model_all[loadOptions.skip <= c.model_all.length ? (loadOptions.skip - 1) : (c.model_all.length - 1)];
                        const last = c.model_all[params.startRow <= c.model_all.length ? (params.startRow - 1) : (c.model_all.length - 1)];

                        const initialKeyValue = encodeURIComponent(last[sortCol]);

                        let keys = '"' + initialKeyValue + '"'; // add the initial value to handle asc/desc properly

                        if (c.sortDef.AdditionalKeyFields)
                            for (let x = 0; x < c.sortDef.AdditionalKeyFields.length; x++)
                                keys += ',"' + last[c.sortDef.AdditionalKeyFields[x]].replace(/\\/g, "\\\\") + '"';

                        c.startKeyList = "[" + keys + "]";
                        // pageSize++;
                        skipFirst = (c.Vars.DisableSkipFirst === undefined || !c.Vars.DisableSkipFirst);
                    }

                    // Append sort column, startKey, maxRows, and descending flag
                    url += (url.indexOf('?') == -1 ? "?" : "&") + // Example: /Apps/FilesExt/
                        "sortcol=" + sortCol.toLowerCase() + "&" + // Example: filenamenopath
                        "startKey=" + c.startKeyList + "&" +
                        "maxRows=" + (skipFirst ? pageSize + 1 : pageSize) + "&" +
                        "descending=" + (descending ? 'true' : 'false');

                    //Ex:
                    //url += '?sortcol=objecttype&startKey=["",""]&maxRows=100&descending=false';

                    let model = await utils.api.get(url, false, false);

                    let totalCount; // = 10000;
                    

                    if (skipFirst)
                        model.shift();

                    // Track all rows in a master array
                    if (c.model_all.length > 0) {
                        // we have already run once...
                        if (c.model_all.length > params.startRow) {
                            // override all spots in model_all with the model
                            c.model_all.splice(params.startRow, model.length, ...model);
                        }
                        else  {
                            c.model_all = c.model_all.concat(model);
                        }
                        
                    }
                    else {
                        c.model_all = model;
                    }

                    
                    if (model.length < pageSize) {
                        // Hit the end, insure the total count is correct
                        totalCount = c.model_all.length;// - (skipFirst ? 1 : 0);
                        if (totalCount < 0) totalCount = 0;
                    }
                    //c.model_all = c.model_all.concat(model);

                    utils.debug(`InfiniteGrid successCallback(model:${model.length}, totalCount:${totalCount})`);
                    utils.debug(`InfiniteGrid successCallback(model:${model.length}, pagesize:${pageSize})`);
                    //utils.debug(`model_all: ${JSON.stringify(c.model_all)}`);
                    //utils.debug(`model: ${JSON.stringify(model)}`);
                    utils.debug(`params: ${JSON.stringify(params)}`);
                    params.successCallback(model, totalCount);
                    //return {
                    //    data: model,
                    //    totalCount: totalCount,
                    //    summary: 0,
                    //    groupCount: 0
                    //};
                }
                }
                catch (er) {
                    // Without this the datasource never resolves the block, so every cell in it
                    // renders the AgContentDef loading placeholder ("One moment please...") for ever.
                    console.error('InfiniteGrid.getRows failed:');
                    console.error(JSON.stringify(params));
                    console.error(er);
                    params.failCallback();
                }
            },
        };
    },
    //Mounted Replaced with preRenderComplete
    computed: {
    },
    methods: {
        preRenderComplete() {
            this.finishRenderHandler(this);
        },
        async Refresh(clearSelectedRows) {
            if (this.gridApi) {
                if (clearSelectedRows) {
                    this.gridApi.deselectAll();
                }
                
                this.gridApi.purgeInfiniteCache();
            }
        }
    },
    props: {
    },
    render() {
        if (!this.todisplay)
            return null;

        const style = {
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            minHeight: "400px",
            padding: '8px',
            ...this.sizeStyle,
        };

        return (
            <div
                key={this.unique_id + '_main'}
                v-show={this.isvisible}
                class={{ 'c-InfiniteGrid': true, [`c-name-${this.name || 'unnamed'}`]: true, 'ag-theme-alpine': true }}
                style={style}
            >
                {this.getGridHeaders()}
                <AgGridVue
                    key={this.unique_id}
                    ref="agGridRef"
                    class={{ 'ag-theme-alpine': true }}
                    style={{ display: 'flex', flexDirection: 'column', flexGrow: '1' }}
                    rowModelType="infinite"
                    columnDefs={this.agColumnDefs}
                    datasource={this.dataSource}
                    rowSelection={this.getRowSelectionConfig()}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                    quickFilterText={this.searchText}
                    suppressScrollOnNewData={true}
                    suppressDragLeaveHidesColumns={true}
                    suppressCellSelection={true}
                    pagination={this.pagination}
                    paginationAutoPageSize={this.pagination}
                    getRowClass={this.getRowClass}
                    maintainColumnOrder={true}

                    onGridReady={this.onGridReady}
                    onFirstDataRendered={(e) => this.onFirstDataRendered(e)}
                    onSelectionChanged={(e) => this.rowSelectionChanged(e)}
                    onSortChanged={this.onSaveGridSortState}
                    onColumnVisible={this.onSaveGridColumnState}
                    onColumnPinned={this.onSaveGridColumnState}
                    onColumnResized={this.onSaveGridColumnState}
                    onColumnMoved={this.onSaveGridColumnState}
                    onColumnRowGroupChanged={this.onSaveGridColumnState}
                >
                </AgGridVue>
            </div>
        );
    }
});